import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Key, Plus, Trash2, Copy, CheckCircle2, AlertTriangle, X, Loader2,
    Download,
} from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";
import DeployAgentPanel from "@/components/DeployAgentModal";
import { useAuth } from "@/context/AuthContext";

/**
 * Admin-only management of machine-to-machine API tokens.
 *
 * Tokens (`mb_…`) are the audit-friendly alternative to hard-coding the
 * human admin password in CI pipelines or integrations. Pattern matches
 * GitHub / Stripe personal-access-tokens UX: one-shot reveal, prefix-only
 * display afterwards, revoke with a single click.
 */
const EXPIRY_PRESETS = [
    { v: 30, label: "30 days" },
    { v: 90, label: "90 days" },
    { v: 365, label: "1 year" },
    { v: null, label: "Never" },
];

export default function ApiTokens() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const tab = params.get("tab") === "deploy-agent" ? "deploy-agent" : "tokens";
    const setTab = (t) => setParams(t === "deploy-agent" ? { tab: "deploy-agent" } : {}, { replace: true });
    const canDeploy = user?.role === "admin" || user?.role === "superadmin";

    return (
        <div className="mx-auto max-w-[1100px] px-6 py-8">
            <div className="mb-5">
                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                    <Key size={12} /> Infrastructure access
                </div>
                <h2 className="mt-1 font-display text-3xl font-bold tracking-tight">API & Deployment</h2>
            </div>

            <div className="mb-5 flex gap-1 border-b border-border" data-testid="api-tokens-tabs">
                <button
                    onClick={() => setTab("tokens")}
                    data-testid="api-tokens-tab-tokens"
                    className={[
                        "border-b-2 px-4 py-2 text-sm font-medium",
                        tab === "tokens"
                            ? "border-brand text-brand"
                            : "border-transparent text-muted-foreground hover:text-foreground",
                    ].join(" ")}
                >
                    <Key size={12} className="mr-1.5 inline" /> Tokens
                </button>
                {canDeploy && (
                    <button
                        onClick={() => setTab("deploy-agent")}
                        data-testid="api-tokens-tab-deploy-agent"
                        className={[
                            "border-b-2 px-4 py-2 text-sm font-medium",
                            tab === "deploy-agent"
                                ? "border-brand text-brand"
                                : "border-transparent text-muted-foreground hover:text-foreground",
                        ].join(" ")}
                    >
                        <Download size={12} className="mr-1.5 inline" /> Download Agent
                    </button>
                )}
            </div>

            {tab === "deploy-agent" && canDeploy ? <DeployAgentTab /> : <TokensTab />}
        </div>
    );
}

function DeployAgentTab() {
    return (
        <div data-testid="deploy-agent-tab">
            <p className="mb-5 max-w-2xl text-sm text-muted-foreground">
                Get a MoovBytes agent running on a real host in under a minute — pick an OS below,
                grab the installer, and paste in your credentials when prompted.
            </p>
            <DeployAgentPanel chrome />
        </div>
    );
}

// Exported so Portal Settings' "API Tokens" tab can render just the token
// management UI directly, without the Deploy Agent sub-tab that lives here
// (Deploy Agent / Download Agent was removed from the app's nav entirely,
// not relocated to the Portal — see Layout.jsx NAV_GROUPS history).
export function TokensTab() {
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState("");
    const [newExpiry, setNewExpiry] = useState(90);
    const [reveal, setReveal] = useState(null); // {token, prefix, name, expires_at} shown once
    const [copied, setCopied] = useState(false);
    const [tenantSlug, setTenantSlug] = useState(null);
    const [slugCopied, setSlugCopied] = useState(false);

    const load = async () => {
        try {
            const { data } = await http.get("/auth/api-tokens");
            setTokens(data);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load API tokens"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // Tenant slug isn't part of the token payload itself, but several
        // integrations (SSO/SCIM URLs, multi-tenant API calls) need it
        // alongside a token, so it's surfaced right here rather than
        // making the operator hunt for it in Settings > Organization.
        http.get("/organization").then(({ data }) => setTenantSlug(data.slug)).catch(() => {});
    }, []);

    const copySlug = async () => {
        if (!tenantSlug) return;
        try {
            await navigator.clipboard.writeText(tenantSlug);
            setSlugCopied(true);
            setTimeout(() => setSlugCopied(false), 1500);
        } catch { /* ignore */ }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setCreating(true);
        try {
            const { data } = await http.post("/auth/api-tokens", {
                name: newName.trim(),
                expires_in_days: newExpiry,
            });
            setReveal(data);
            setNewName("");
            setNewExpiry(90);
            load();
        } catch (err) {
            toast.error(errMsg(err, "Failed to create token"));
        } finally {
            setCreating(false);
        }
    };

    const revoke = async (id, name) => {
        if (!window.confirm(`Revoke "${name}"? Any CI job using this token will immediately lose access.`)) return;
        try {
            await http.delete(`/auth/api-tokens/${id}`);
            toast.success("Token revoked");
            load();
        } catch (err) {
            toast.error(errMsg(err, "Failed to revoke"));
        }
    };

    const copyReveal = async () => {
        try {
            await navigator.clipboard.writeText(reveal.token);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch { /* ignore */ }
    };

    return (
        <div data-testid="api-tokens-tab">
            <p className="mb-4 max-w-xl text-sm text-muted-foreground">
                Long-lived tokens for CI jobs and integrations. Presented in{" "}
                <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">Authorization: Bearer</code>{" "}
                or{" "}
                <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">X-API-Token</code> headers —
                inherit the creator's role. Revoke anytime.
            </p>

            {tenantSlug && (
                <div className="mb-4 flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs" data-testid="api-tokens-tenant-slug">
                    <span className="font-medium text-muted-foreground">Tenant slug</span>
                    <code className="font-mono text-[11px]">{tenantSlug}</code>
                    <button
                        onClick={copySlug}
                        aria-label="Copy tenant slug"
                        className="ml-auto flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    >
                        {slugCopied ? <CheckCircle2 size={13} /> : <Copy size={13} />}
                    </button>
                </div>
            )}

            {/* Create form */}
            <form
                onSubmit={handleCreate}
                className="mb-6 rounded-xl border border-border bg-surface p-4"
                data-testid="api-tokens-create-form"
            >
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                    <Plus size={14} className="text-[#1D4ED8]" /> Issue new token
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px_auto]">
                    <input
                        type="text"
                        placeholder="Descriptive name (e.g. `CI release pipeline`)"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        maxLength={80}
                        className="mb-focus rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="api-tokens-name-input"
                        required
                    />
                    <select
                        value={newExpiry === null ? "never" : String(newExpiry)}
                        onChange={(e) => setNewExpiry(e.target.value === "never" ? null : Number(e.target.value))}
                        className="mb-focus rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="api-tokens-expiry-select"
                    >
                        {EXPIRY_PRESETS.map((p) => (
                            <option key={p.label} value={p.v === null ? "never" : p.v}>{p.label}</option>
                        ))}
                    </select>
                    <button
                        type="submit"
                        disabled={creating || !newName.trim()}
                        className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#1D4ED8] to-[#1E40AF] px-4 py-2 text-sm font-semibold text-white shadow transition disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid="api-tokens-create-btn"
                    >
                        {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                        Generate
                    </button>
                </div>
            </form>

            {/* One-shot reveal modal */}
            {reveal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    onClick={() => setReveal(null)}
                    data-testid="api-tokens-reveal-modal"
                >
                    <div
                        className="w-full max-w-xl rounded-2xl border border-border bg-surface p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]">
                                    <CheckCircle2 size={20} />
                                </div>
                                <div>
                                    <h3 className="font-display text-lg font-bold">Token generated</h3>
                                    <p className="mt-0.5 text-xs text-muted-foreground">
                                        {reveal.name}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setReveal(null)}
                                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                                aria-label="Close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="mt-4 flex items-start gap-2 rounded-md border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-3 text-[12px]">
                            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[#f59e0b]" />
                            <span>
                                Copy this token <b>now</b>. It will never be shown again — revoke &
                                regenerate if you lose it.
                            </span>
                        </div>

                        <div className="mt-4 flex items-center gap-2 rounded-md bg-[#0b0f1a] p-3 font-mono text-[11px] text-[#d1d5db]">
                            <code className="flex-1 break-all" data-testid="api-tokens-reveal-value">{reveal.token}</code>
                            <button
                                onClick={copyReveal}
                                className="inline-flex items-center gap-1 rounded bg-white/10 px-2 py-1 text-[10px] font-semibold text-white hover:bg-white/20"
                                data-testid="api-tokens-reveal-copy"
                            >
                                {copied ? <CheckCircle2 size={11} /> : <Copy size={11} />}
                                {copied ? "Copied" : "Copy"}
                            </button>
                        </div>

                        <div className="mt-4 space-y-1 text-[11px] text-muted-foreground">
                            <div>Example with <code>curl</code>:</div>
                            <pre className="overflow-x-auto rounded-md bg-muted/40 p-2 font-mono">
{`curl -H "Authorization: Bearer ${reveal.token}" \\
     ${window.location.origin}/api/v1/agents/assets`}
                            </pre>
                        </div>
                    </div>
                </div>
            )}

            {/* Token list */}
            <div className="rounded-xl border border-border bg-surface" data-testid="api-tokens-list">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <div className="font-display text-base font-bold">Active tokens</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        {tokens.length} total
                    </div>
                </div>
                {loading ? (
                    <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                        <Loader2 size={14} className="animate-spin" /> Loading…
                    </div>
                ) : tokens.length === 0 ? (
                    <div className="px-6 py-10 text-center text-sm text-muted-foreground">
                        No tokens yet. Generate one above for your next CI deployment.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead>
                                <tr className="bg-muted/30 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    <th className="px-4 py-2">Name</th>
                                    <th className="px-4 py-2">Token preview</th>
                                    <th className="px-4 py-2">Permissions</th>
                                    <th className="px-4 py-2">Created</th>
                                    <th className="px-4 py-2">Last used</th>
                                    <th className="px-4 py-2">Expires</th>
                                    <th className="px-4 py-2">Status</th>
                                    <th className="px-4 py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tokens.map((t) => (
                                    <TokenRow key={t.id} t={t} onRevoke={() => revoke(t.id, t.name)} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const TOKEN_STATUS_META = {
    active: { label: "Active", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
    revoked: { label: "Revoked", cls: "bg-muted text-muted-foreground" },
    expired: { label: "Expired", cls: "bg-muted text-muted-foreground" },
};

function TokenRow({ t, onRevoke }) {
    const expired = t.expires_at && new Date(t.expires_at) < new Date();
    const status = t.revoked ? "revoked" : expired ? "expired" : "active";
    const meta = TOKEN_STATUS_META[status];

    return (
        <tr className="border-t border-border/60" data-testid={`api-tokens-row-${t.id}`}>
            <td className="px-4 py-2.5 font-semibold">{t.name}</td>
            <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{t.prefix}••••••••••••</td>
            <td className="px-4 py-2.5 text-muted-foreground">Inherits your role</td>
            <td className="px-4 py-2.5 text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</td>
            <td className="px-4 py-2.5 text-muted-foreground">
                {t.last_used_at ? new Date(t.last_used_at).toLocaleDateString() : "Never"}
            </td>
            <td className="px-4 py-2.5 text-muted-foreground">
                {t.expires_at ? new Date(t.expires_at).toLocaleDateString() : "Never"}
            </td>
            <td className="px-4 py-2.5">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`}>{meta.label}</span>
            </td>
            <td className="px-4 py-2.5 text-right">
                {!t.revoked && (
                    <button
                        onClick={onRevoke}
                        aria-label={`Revoke token ${t.name}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold text-[hsl(var(--destructive))] transition hover:border-[hsl(var(--destructive))]/50 hover:bg-[hsl(var(--destructive))]/5"
                        data-testid={`api-tokens-revoke-${t.id}`}
                    >
                        <Trash2 size={12} /> Revoke
                    </button>
                )}
            </td>
        </tr>
    );
}
